import { toast } from 'react-toastify';
import { CreateSupplierBody, Supplier, UpdateSupplierBody } from '@core/models/supplier.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import SupplierService from 'services/api/supplier.service';

// -------------------------------------------------------------------------

interface ISupplierState {
    loading: boolean;
    createLoading: boolean;
    updateLoading: boolean;
    deleteLoading: boolean;
    getByIdLoading: boolean;
    error: IErrorModel | undefined;
    suppliers: Supplier[];
    supplier: Supplier | null;
}

const initialState: ISupplierState = {
    loading: false,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,
    getByIdLoading: false,
    error: {} as IErrorModel,
    suppliers: [],
    supplier: null,
};

// -------------------------------------------------------------------------

export const getSuppliersThunk = createAsyncThunk<Supplier[], void, { rejectValue: IErrorModel }>(
    `/supplier/getSuppliers`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await SupplierService.getSuppliers();
            return response.data.suppliers;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch suppliers', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const getSupplierByIdThunk = createAsyncThunk<Supplier, number, { rejectValue: IErrorModel }>(
    `/supplier/getSupplierById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await SupplierService.getSupplierById(id);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to fetch supplier', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const createSupplierThunk = createAsyncThunk<
    ISuccessResponse,
    CreateSupplierBody,
    { rejectValue: IErrorModel }
>(`/supplier/createSupplier`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await SupplierService.createSupplier(body);
        toast.success(response.data.message || 'Supplier created successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to create supplier', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const updateSupplierThunk = createAsyncThunk<
    ISuccessResponse,
    UpdateSupplierBody,
    { rejectValue: IErrorModel }
>(`/supplier/updateSupplier`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await SupplierService.updateSupplier(body);
        toast.success(response.data.message || 'Supplier updated successfully', { autoClose: 5000 });
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to update supplier', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const deleteSupplierThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/supplier/deleteSupplier`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await SupplierService.deleteSupplier(id);
            toast.success(response.data.message || 'Supplier deleted successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to delete supplier', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const supplierSlice = createSlice({
    name: `supplier`,
    initialState,
    reducers: {
        clearSupplier(state) {
            state.supplier = null;
        },
    },
    extraReducers: (builder) => {
        // Get Suppliers
        builder.addCase(getSuppliersThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getSuppliersThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.suppliers = action.payload;
        });
        builder.addCase(getSuppliersThunk.rejected, (state, action) => {
            state.loading = false;
            state.suppliers = [];
            state.error = action.payload;
        });

        // Get Supplier By Id
        builder.addCase(getSupplierByIdThunk.pending, (state) => {
            state.getByIdLoading = true;
        });
        builder.addCase(getSupplierByIdThunk.fulfilled, (state, action) => {
            state.getByIdLoading = false;
            state.supplier = action.payload;
        });
        builder.addCase(getSupplierByIdThunk.rejected, (state, action) => {
            state.getByIdLoading = false;
            state.error = action.payload;
        });

        // Create Supplier
        builder.addCase(createSupplierThunk.pending, (state) => {
            state.createLoading = true;
        });
        builder.addCase(createSupplierThunk.fulfilled, (state) => {
            state.createLoading = false;
        });
        builder.addCase(createSupplierThunk.rejected, (state, action) => {
            state.createLoading = false;
            state.error = action.payload;
        });

        // Update Supplier
        builder.addCase(updateSupplierThunk.pending, (state) => {
            state.updateLoading = true;
        });
        builder.addCase(updateSupplierThunk.fulfilled, (state) => {
            state.updateLoading = false;
        });
        builder.addCase(updateSupplierThunk.rejected, (state, action) => {
            state.updateLoading = false;
            state.error = action.payload;
        });

        // Delete Supplier
        builder.addCase(deleteSupplierThunk.pending, (state) => {
            state.deleteLoading = true;
        });
        builder.addCase(deleteSupplierThunk.fulfilled, (state) => {
            state.deleteLoading = false;
        });
        builder.addCase(deleteSupplierThunk.rejected, (state, action) => {
            state.deleteLoading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default supplierSlice;
export const { clearSupplier } = supplierSlice.actions;
export const supplierReducer = supplierSlice.reducer;

